import React, { useEffect, useMemo, useRef, useState } from "react";
// No layout wrapper per request
import { auth, database, firestore } from "../../../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import {
  ref as rtdbRef,
  onValue,
  off as rtdbOff,
  set as rtdbSet,
  remove as rtdbRemove,
  push as rtdbPush,
  get as rtdbGet,
  update as rtdbUpdate,
} from "firebase/database";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query as fsQuery,
} from "firebase/firestore";
import toast from "react-hot-toast";
import PaymentValidationModal from "./PaymentValidationModal";
import { API_BASE_URL } from "../../../utils/apiBase";
import { countSameDayPhoneDuplicates } from "../../../utils/paymentDuplicateGuard";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
} from "recharts";

/* -------------------------
  Tariff utilities
   ------------------------- */

const calcTariff = (amount, method) => {
  const m = String(method).toLowerCase();
  const a = Number(amount) || 0;

  if (m.includes("m-pesa")) {
    if (a <= 100) return 0;
    if (a <= 1500) return 15;
    if (a <= 5000) return 20;
    if (a <= 20000) return 23;
    if (a <= 50000) return 25;
    if (a <= 100000) return 28;
    if (a <= 250000) return 45;
    return 0;
  }

  if (m.includes("kcb")) {
    if (a <= 100) return 0;
    if (a <= 1000) return 15;
    if (a <= 2500) return 48;
    if (a <= 10000) return 48;
    if (a <= 100000) return 50;
    if (a <= 250000) return 70;
    return 0;
  }

  return 0;
};

const getStandardTariff = (amount) => {
  const a = Number(amount) || 0;
  if (a <= 100) return 0;
  if (a <= 1000) return 13;
  if (a <= 1500) return 23;
  if (a <= 2500) return 33;
  if (a <= 3500) return 53;
  if (a <= 5000) return 57;
  if (a <= 7500) return 78;
  if (a <= 10000) return 90;
  if (a <= 15000) return 100;
  if (a <= 20000) return 105;
  if (a <= 35000) return 108;
  if (a <= 50000) return 108;
  if (a <= 250000) return 108;
  return 0;
};

const resolvePhoneNumber = (entry, farmersMap) => {
  if (!entry) return "";
  if (entry.PhoneNumber) return entry.PhoneNumber;
  if (entry.MSISDN) return entry.MSISDN;
  if (entry.phoneNumber) return entry.phoneNumber;

  const other = entry.OtherName || entry.otherName;
  const farmer = other && farmersMap ? farmersMap[other] : null;
  if (farmer) {
    return (
      farmer.PhoneNumber ||
      farmer.MSISDN ||
      farmer.phoneNumber ||
      farmer.phone ||
      ""
    );
  }
  return "";
};

const MAX_PHONE_LOOKUP_LIMIT =
  Number(process.env.REACT_APP_B2C_PHONE_LIMIT) || 200;
const PHONE_HISTORY_MONTHS =
  Number(process.env.REACT_APP_B2C_HISTORY_MONTHS) || 6;
const VOICE_ENABLED_DEFAULT = process.env.REACT_APP_PAYMENT_VOICE === "true";

const getPaymentKey = (payment) =>
  payment?._key ||
  payment?.Id ||
  payment?.id ||
  payment?.CloudName ||
  payment?.OtherName ||
  payment?.otherName ||
  "";

const getTransactionTime = (tx) => {
  const value = tx?.transactionDate || tx?.processedAt || tx?.completion_time;
  const date = value?.toDate ? value.toDate() : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
};

const classifyPatternWindow = (date) => {
  const dow = date.getDay();
  const dom = date.getDate();
  if ([30, 31, 1, 2].includes(dom)) return "monthly_window";
  if (dom >= 15 && dom <= 18) return "half_month_window";
  if ([4, 5].includes(dow)) return "weekly_window";
  return "other";
};

const buildFeatureVector = ({ phone, amount, ts, history }) => {
  const tsDate = new Date(ts);
  const phoneHistory = (history || [])
    .filter((h) => getTransactionTime(h) < tsDate.getTime())
    .sort((a, b) => getTransactionTime(a) - getTransactionTime(b));

  if (!phoneHistory.length) {
    return { status: "no_history" };
  }

  const numericAmounts = phoneHistory.map(
    (h) => Number(h.transactionAmount || h.amount || 0) || 0
  );
  const countPrev = numericAmounts.length;
  const sumPrev = numericAmounts.reduce((sum, v) => sum + v, 0);
  const meanPrev = sumPrev / countPrev;
  const variance =
    numericAmounts.reduce((sum, v) => sum + Math.pow(v - meanPrev, 2), 0) /
    Math.max(countPrev, 1);
  let stdPrev = Math.sqrt(variance);
  if (!Number.isFinite(stdPrev) || stdPrev === 0) {
    stdPrev = Math.sqrt(
      numericAmounts.reduce((sum, v) => sum + Math.pow(v - meanPrev, 2), 0) /
        Math.max(numericAmounts.length, 1)
    );
    if (!Number.isFinite(stdPrev) || stdPrev === 0) {
      stdPrev = 1;
    }
  }

  const dow = tsDate.getDay();
  const dom = tsDate.getDate();
  const hour = tsDate.getHours();
  const txWindow = classifyPatternWindow(tsDate);

  const patternCounts = phoneHistory.reduce((acc, h) => {
    const d = h.transactionDate || h.processedAt || h.completion_time;
    const dateObj = d?.toDate ? d.toDate() : new Date(d);
    const bucket = classifyPatternWindow(dateObj);
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});
  const dominant =
    Object.keys(patternCounts).reduce((best, key) => {
      if (best === null) return key;
      return patternCounts[key] > (patternCounts[best] || 0) ? key : best;
    }, null) || "other";

  const matchesDominant = txWindow === dominant ? 1 : 0;
  const amountZ = (amount - meanPrev) / (stdPrev || 1);

  const features = {
    amount,
    day_of_week: dow,
    day_of_month: dom,
    hour,
    mean_amount_prev: meanPrev,
    std_amount_prev: stdPrev,
    amount_zscore: amountZ,
    count_prev: countPrev,
    matches_dominant_window: matchesDominant,
    tx_pattern_window_other: txWindow === "other" ? 1 : 0,
    tx_pattern_window_weekly_window: txWindow === "weekly_window" ? 1 : 0,
  };

  return {
    status: "ok",
    features,
    historyStats: {
      countPrev,
      meanPrev,
      stdPrev,
      amountZ,
      dominantWindow: dominant,
      txWindow,
    },
  };
};

const fetchPhoneHistory = async (phone) => {
  if (!firestore || !phone) return [];
  const cursor = new Date();
  const records = [];
  const windows = [];

  for (let i = 0; i < PHONE_HISTORY_MONTHS; i += 1) {
    windows.push({
      year: String(cursor.getFullYear()),
      month: String(cursor.getMonth() + 1).padStart(2, "0"),
    });
    cursor.setMonth(cursor.getMonth() - 1);
  }

  await Promise.all(
    windows.map(async ({ year, month }) => {
      const colRef = collection(
        firestore,
        "b2c_transactions_by_phone",
        phone,
        year,
        month,
        "tx"
      );
      try {
        const snap = await getDocs(
          fsQuery(colRef, orderBy("processedAt", "desc"), limit(MAX_PHONE_LOOKUP_LIMIT))
        );
        snap.forEach((docSnap) => {
          records.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
      } catch (err) {
        console.error("Failed to fetch phone history", err);
      }
    })
  );

  records.sort((a, b) => getTransactionTime(b) - getTransactionTime(a));
  return records;
};

/* -------------------------
   Sort helper for payment lists
   ------------------------- */
const comparePayments = (a, b, field, dir, farmerView) => {
  if (field === "date") {
    const valA = a.TransactionDate ? new Date(a.TransactionDate).getTime() : 0;
    const valB = b.TransactionDate ? new Date(b.TransactionDate).getTime() : 0;
    return dir === "asc" ? valA - valB : valB - valA;
  }
  if (field === "amount") {
    const valA = Number(a.Amount || 0);
    const valB = Number(b.Amount || 0);
    return dir === "asc" ? valA - valB : valB - valA;
  }
  // default: name
  const nameA = farmerView
    ? a.ReceiptNo || a.CloudName || a.OtherName || a.Id || ""
    : a.CloudName || a.OtherName || a.ReceiptNo || a.Id || "";
  const nameB = farmerView
    ? b.ReceiptNo || b.CloudName || b.OtherName || b.Id || ""
    : b.CloudName || b.OtherName || b.ReceiptNo || b.Id || "";
  const cmp = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  return dir === "asc" ? cmp : -cmp;
};

/* -------------------------
   Small helper UIs
   ------------------------- */
const SummaryCard = ({ title, children }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm border">
    <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
    <div>{children}</div>
  </div>
);

const PaymentRow = ({
  p,
  onEdit,
  onDelete,
  onPay,
  isAdminView,
  compact,
  validatedFarmers,
  canPay = true,
  paying = false,
}) => {
  const amount = Number(p.Amount || 0);
  const farmerKey = p.OtherName || p.otherName || p.CloudName;
  const farmer = farmerKey && validatedFarmers ? validatedFarmers[farmerKey] : null;
  const resolvedMode = farmer?.Mode || farmer?.mode || p.Mode || "";
  const ourTariff = calcTariff(amount, resolvedMode);
  const otherTariff = getStandardTariff(amount);
  const savings = otherTariff - ourTariff;

  return (
    <div
      className={`flex items-center justify-between gap-4 p-3 border-b ${
        compact ? "flex-wrap" : ""
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-800 truncate">
          {p.CloudName || p.OtherName || p.ReceiptNo || p.Id}
        </div>
        {!compact && (
          <>
            <div className="text-xs text-gray-500 truncate">{p.Details}</div>
            <div className="text-xs text-gray-400">
              {p.TransactionDate
                ? new Date(p.TransactionDate).toLocaleString()
                : "—"}
            </div>
          </>
        )}
      </div>

      <div className="w-36 text-right">
        <div className="font-semibold text-cyan-700">
          KES {amount.toLocaleString()}
        </div>
        {!compact && (
          <div className="text-xs text-gray-400">{p.Mode || "—"}</div>
        )}
      </div>

      {!compact && (
        <>
          <div className="w-36 text-right">
            <div className="text-sm text-gray-700">Our tariff</div>
            <div className="font-medium">
              KES {ourTariff.toLocaleString()}
            </div>
          </div>

          <div className="w-36 text-right">
            <div className="text-sm text-gray-700">Other means</div>
            <div className="font-medium">
              KES {otherTariff.toLocaleString()}
            </div>
            <div className="text-xs text-green-700 mt-1">
              Save KES {savings.toLocaleString()}
            </div>
          </div>
        </>
      )}

      <div className="flex items-center gap-2">
        {isAdminView && (
          <>
            <button
              onClick={() => onEdit(p)}
              className="px-3 py-1 bg-amber-500 text-white rounded text-sm"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(p)}
              className="px-3 py-1 bg-red-500 text-white rounded text-sm"
            >
              Delete
            </button>
            <button
              onClick={() => onPay && onPay(p)}
              className="px-3 py-1 bg-emerald-600 text-white rounded text-sm disabled:opacity-50"
              disabled={!onPay || !canPay || paying}
            >
              {paying ? "Paying..." : canPay ? "Pay" : "Insufficient"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* -------------------------
  Modals
   ------------------------- */
const RecordPaymentModal = ({
  open,
  onClose,
  farmer,
  companyName,
  validatedFarmers,
}) => {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("M-Pesa");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!companyName) {
      toast.error("Company not loaded");
      return;
    }
    if (!farmer?.otherName) {
      toast.error("Farmer not selected");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error("Enter valid amount");
      return;
    }

    setLoading(true);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const monthName = now.toLocaleString("en-US", { month: "long" });
      const day = now.getDate().toString().padStart(2, "0");

      const id =
        rtdbPush(rtdbRef(database, "temp")).key || `${Date.now()}`;

      const phoneNumber = resolvePhoneNumber(
        {
          ...farmer,
          OtherName: farmer.otherName,
          PhoneNumber: farmer.PhoneNumber || farmer.phoneNumber,
        },
        validatedFarmers
      );

      const paymentData = {
        Id: id,
        OtherName: farmer.otherName,
        CloudName: farmer.name,
        PhoneNumber: phoneNumber,
        Status: "Pending",
        Details: details || "Payment",
        CompanyName: companyName,
        AuthorizedBy: auth.currentUser?.email || "unknown",
        TransactionDate: now.toISOString(),
        Amount: String(amount),
        ReceiptNo: `RCPT-${Math.floor(Math.random() * 100000)}`,
        Mode: mode,
      };

      // 1️⃣ Save to Farmer record
      const clientPath = `Companies/${companyName}/FarmerRecords/${farmer.otherName}/Payments/${year}/${monthName}/${id}`;
      await rtdbSet(rtdbRef(database, clientPath), paymentData);

      // 2️⃣ Save to Company Payments
      const companyPath = `Companies/${companyName}/Payments/${year}/${monthName}/${day}/${id}`;
      await rtdbSet(rtdbRef(database, companyPath), paymentData);

      if (mode === "M-Pesa") {
        // 3️⃣ Save to NewPaymentRecord (for approval)
        const newPaymentPath = `Companies/${companyName}/NewPaymentRecord/${id}`;
        await rtdbSet(rtdbRef(database, newPaymentPath), paymentData);
      }

      toast.success("Payment recorded (pending approval)");
      onClose();
    } catch (err) {
      console.error("RecordPayment error", err);
      toast.error("Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-96 shadow-lg">
        <h3 className="text-lg font-semibold text-cyan-700 mb-3">
          Record Payment
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600">Farmer</label>
            <div className="text-sm font-medium">{farmer?.name || "—"}</div>
          </div>

          <div>
            <label className="block text-xs text-gray-600">
              Amount (KES)
            </label>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600">Mode</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option>M-Pesa</option>
              <option>Cash</option>
              <option>KCB</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600">Details</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button className="px-3 py-2 rounded border" onClick={onClose}>
            Cancel
          </button>
          <button
            className="px-3 py-2 rounded bg-cyan-600 text-white"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditPaymentModal = ({ open, onClose, payment, companyName, onSaved }) => {
  const [amount, setAmount] = useState(payment?.Amount || "");
  const [details, setDetails] = useState(payment?.Details || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAmount(payment?.Amount || "");
    setDetails(payment?.Details || "");
  }, [payment]);

  if (!open || !payment) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      const path = `Companies/${companyName}/NewPaymentRecord/${payment.Id}`;
      const updated = { ...payment, Amount: String(amount), Details: details };
      await rtdbSet(rtdbRef(database, path), updated);

      toast.success("Payment updated");
      onSaved && onSaved(updated);
      onClose();
    } catch (err) {
      console.error("EditPayment error", err);
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-96 shadow-lg">
        <h3 className="text-lg font-semibold text-cyan-700 mb-3">
          Edit Payment
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600">
              Amount (KES)
            </label>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600">Details</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button className="px-3 py-2 rounded border" onClick={onClose}>
            Cancel
          </button>
          <button
            className="px-3 py-2 rounded bg-amber-500 text-white"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------
   Main Payments Page
   ------------------------- */
const PaymentsPage = ({ forcedCompanyName, compactView = false }) => {
  const [profile, setProfile] = useState(null);
  const [companyName, setCompanyName] = useState(forcedCompanyName || "");
  const [role, setRole] = useState(
    forcedCompanyName ? "admin" : ""
  ); // companyAdmin | farmer | employee | admin | etc

  // Admin company pending/new approvals
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [balances, setBalances] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [payingId, setPayingId] = useState(null);
  const inFlightPaymentKeysRef = useRef(new Set());
  const [payAllBusy, setPayAllBusy] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(VOICE_ENABLED_DEFAULT);
  const [reviewState, setReviewState] = useState({
    currentKey: null,
    steps: [],
    decision: "idle",
    historyStats: null,
    modelScore: null,
    flags: { history: false, model: false, duplicate: false },
    phone: "",
    payment: null,
  });

  // Farmer view state
  const [farmerPayments, setFarmerPayments] = useState([]);
  const [farmerLoading, setFarmerLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [balance, setBalance] = useState(0);

  // Company historical payments (date selector)
  const [companyDate, setCompanyDate] = useState(""); // yyyy-mm-dd
  const [companyRecords, setCompanyRecords] = useState([]);
  const [companyRecordsLoading, setCompanyRecordsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");

  // UI state
  const [validatedFarmers, setValidatedFarmers] = useState({});
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordTargetFarmer, setRecordTargetFarmer] = useState(null);
  const [editPayment, setEditPayment] = useState(null);
  const [crossPayment, setCrossPayment] = useState(null);

  // Sort & pagination state for the payments list
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Determine role & company from Firestore user doc
  useEffect(() => {
    if (forcedCompanyName) {
      setCompanyName(forcedCompanyName);
      setRole("admin");
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setProfile(null);
        setCompanyName("");
        setRole("");
        return;
      }
      try {
        const userDoc = await getDoc(doc(firestore, "users", u.uid));
        const data = userDoc.exists() ? userDoc.data() : {};
        setProfile(data || null);

        // normalize accountType -> internal role
        const raw = (data?.accountType || "")
          .toLowerCase()
          .replace(/\s+/g, "");
        let normalizedRole = raw;
        if (raw === "companyadmin" || raw === "company_admin") {
          normalizedRole = "companyAdmin";
        } else if (raw === "employee" || raw === "manager") {
          normalizedRole = "employee";
        } else if (raw === "farmer") {
          normalizedRole = "farmer";
        } else if (raw === "admin" || raw === "superadmin") {
          normalizedRole = "admin";
        }
        setRole(normalizedRole);

        // prefer a canonical companyKey if you have one
        setCompanyName(data?.companyKey || data?.companyName || "");
      } catch (err) {
        console.error("Error loading user profile", err);
      }
    });

    return () => unsub();
  }, [forcedCompanyName]);

  /* -------------------------
     Admin/company: listen for NewPaymentRecord
     ------------------------- */
  useEffect(() => {
    if (!companyName) {
      setPendingPayments([]);
      setPendingLoading(false);
      return;
    }

    setPendingLoading(true);
    const path = `Companies/${companyName}/NewPaymentRecord`;
    const ref = rtdbRef(database, path);

    const handler = (snap) => {
      const val = snap.val() || {};
      const arr = Object.entries(val)
        .map(([key, payment]) => ({ ...payment, _key: key }))
        .sort((a, b) =>
          (b?.TransactionDate || "").localeCompare(
            a?.TransactionDate || ""
          )
        );
      setPendingPayments(arr);
      setPendingLoading(false);
    };

    onValue(ref, handler, (err) => {
      console.error("Pending payments listener error", err);
      setPendingLoading(false);
    });

    return () => {
      rtdbOff(ref, "value", handler);
    };
  }, [companyName]);

  /* -------------------------
     Load validated farmers for phone/mode lookup
      ------------------------- */
  useEffect(() => {
    if (!companyName) {
      setValidatedFarmers({});
      return;
    }

    const path = `Companies/${companyName}/ValidatedFarmers`;
    const ref = rtdbRef(database, path);

    const handler = (snap) => {
      setValidatedFarmers(snap.val() || {});
    };

    onValue(ref, handler, (err) => {
      console.error("ValidatedFarmers listener error", err);
    });

    return () => {
      rtdbOff(ref, "value", handler);
    };
  }, [companyName]);

  /* -------------------------
      Load company balances
      ------------------------- */
  useEffect(() => {
    if (!companyName) {
      setBalances({});
      return;
    }
    const balRef = rtdbRef(
      database,
      `Companies/${companyName}/AccountBalances`
    );
    const handler = (snap) => setBalances(snap.val() || {});
    onValue(balRef, handler, () => setBalances({}));
    return () => rtdbOff(balRef, "value", handler);
  }, [companyName]);

  /* -------------------------
     Farmer: listen for their payments path
     ------------------------- */
  useEffect(() => {
    if (!companyName || role !== "farmer") {
      setFarmerPayments([]);
      setFarmerLoading(false);
      return;
    }

    const other = profile?.otherName;
    if (!other) {
      setFarmerPayments([]);
      setFarmerLoading(false);
      return;
    }

    setFarmerLoading(true);
    const path = `Companies/${companyName}/FarmerRecords/${other}/Payments`;
    const ref = rtdbRef(database, path);

    const handler = (snap) => {
      const val = snap.val() || {};
      const payments = [];
      Object.entries(val).forEach(([year, months]) => {
        Object.entries(months || {}).forEach(([monthName, ids]) => {
          Object.entries(ids || {}).forEach(([id, payment]) => {
            payments.push(payment);
          });
        });
      });
      payments.sort((a, b) =>
        (b?.TransactionDate || "").localeCompare(
          a?.TransactionDate || ""
        )
      );
      setFarmerPayments(payments);
      const bal = payments.reduce(
        (s, p) => s + Number(p.Amount || 0),
        0
      );
      setBalance(bal);
      setFarmerLoading(false);
    };

    onValue(ref, handler, (err) => {
      console.error("Farmer payments listener error", err);
      setFarmerLoading(false);
    });

    return () => {
      rtdbOff(ref, "value", handler);
    };
  }, [companyName, profile, role]);

  /* -------------------------
     Company historical fetch for a chosen date
     ------------------------- */
  const loadCompanyRecordsForDate = async (dateStr) => {
    if (!companyName) {
      toast.error("No company loaded");
      return;
    }
    if (!dateStr) {
      setCompanyRecords([]);
      return;
    }

    setCompanyRecordsLoading(true);
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const monthNumber = date.getMonth() + 1;
      const day = date.getDate();
      const monthName = date.toLocaleString("en-US", { month: "long" });
      const monthNumPadded = String(monthNumber).padStart(2, "0");
      const results = {};

      const fetchPath = async (p) => {
        const snap = await rtdbGet(rtdbRef(database, p));
        const v = snap.val() || {};
        Object.entries(v).forEach(([id, payment]) => {
          results[payment.Id || id] = payment;
        });
      };

      const base = `Companies/${companyName}/Payments/${year}`;
      await Promise.all([
        fetchPath(`${base}/${monthName}/${day}`),
        fetchPath(`${base}/${monthNumPadded}/${day}`),
        fetchPath(`${base}/${monthNumber}/${day}`),
      ]);

      const arr = Object.values(results).sort((a, b) =>
        (b?.TransactionDate || "").localeCompare(
          a?.TransactionDate || ""
        )
      );
      setCompanyRecords(arr);
    } catch (err) {
      console.error("loadCompanyRecordsForDate error", err);
      toast.error("Failed to load records for date");
      setCompanyRecords([]);
    } finally {
      setCompanyRecordsLoading(false);
    }
  };

  useEffect(() => {
    if (!companyRecords.length) {
      setSelectedClientId("");
      return;
    }
    const firstId =
      companyRecords[0].Client_Id ||
      companyRecords[0].ClientId ||
      companyRecords[0].ClientID ||
      "";
    setSelectedClientId((prev) => prev || firstId || "");
  }, [companyRecords]);

  const chartRecords = useMemo(() => {
    const filtered = selectedClientId
      ? companyRecords.filter(
          (p) =>
            (p.Client_Id || p.ClientId || p.ClientID) &&
            (p.Client_Id === selectedClientId ||
              p.ClientId === selectedClientId ||
              p.ClientID === selectedClientId)
        )
      : companyRecords;

    const formatDate = (payment) => {
      if (payment.TransactionDate) {
        const d = new Date(payment.TransactionDate);
        if (!Number.isNaN(d.getTime()))
          return d.toISOString().split("T")[0];
      }
      if (payment.Year && payment.Month && payment.Day) {
        const month = String(payment.Month).padStart(2, "0");
        const day = String(payment.Day).padStart(2, "0");
        return `${payment.Year}-${month}-${day}`;
      }
      return payment.Id || "—";
    };

    return filtered
      .map((p) => ({
        id: p.Id,
        time: formatDate(p),
        amount: Number(p.Amount || 0),
        mode: p.Mode || "Unknown",
        sortDate: p.TransactionDate
          ? new Date(p.TransactionDate).getTime()
          : Date.parse(formatDate(p)) || 0,
      }))
      .sort((a, b) => a.sortDate - b.sortDate);
  }, [companyRecords, selectedClientId]);

  const referenceAmount = chartRecords[0]?.amount || 0;

  const pieData = useMemo(() => {
    if (!chartRecords.length) return [];
    const map = {};
    chartRecords.forEach((r) => {
      const key = r.mode || "Unknown";
      map[key] = (map[key] || 0) + (r.amount || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [chartRecords]);

  const pieColors = [
    "#06b6d4",
    "#10b981",
    "#6366f1",
    "#f59e0b",
    "#ef4444",
  ];

  /* -------------------------
     Derived summaries for admin pending payments
     ------------------------- */
  const pendingSummary = useMemo(() => {
    let totalKcb = 0;
    let totalMpesa = 0;
    let kcbTariff = 0;
    let mpesaTariff = 0;
    let standardTariff = 0;
    let sumAmount = 0;

    pendingPayments.forEach((p) => {
      const amt = Number(p.Amount || 0);
      const farmerKey = p.OtherName || p.otherName || p.CloudName;
      const farmer =
        farmerKey && validatedFarmers ? validatedFarmers[farmerKey] : null;
      const resolvedMode = (farmer?.Mode || farmer?.mode || p.Mode || "").toLowerCase();

      sumAmount += amt;
      standardTariff += getStandardTariff(amt);
      if (resolvedMode.includes("kcb")) {
        totalKcb += amt;
        kcbTariff += calcTariff(amt, "kcb");
      } else if (resolvedMode.includes("m-pesa")) {
        totalMpesa += amt;
        mpesaTariff += calcTariff(amt, "m-pesa");
      }
    });

    const totalTariff = kcbTariff + mpesaTariff;
    const totalSavings = standardTariff - totalTariff;
    const savingsPct =
      sumAmount > 0 ? (totalSavings / sumAmount) * 100 : 0;
    const cumulativeTotal = sumAmount + totalTariff;

    return {
      totalKcb,
      totalMpesa,
      kcbTariff,
      mpesaTariff,
      standardTariff,
      totalTariff,
      totalAmount: sumAmount,
      cumulativeTotal,
      totalSavings,
      savingsPct,
    };
  }, [pendingPayments, validatedFarmers]);

  const companyBankBalance = balances.company_bank?.balance || 0;

  const payableRows = useMemo(() => {
    return pendingPayments.map((p) => {
      const key = getPaymentKey(p);
      const phone = resolvePhoneNumber(p, validatedFarmers);
      const amount = Number(p.Amount || 0);
      const tariff = calcTariff(amount, p.Mode);
      const totalWithFees = amount + tariff;
      const canPay = Boolean(phone) && totalWithFees <= Number(companyBankBalance || 0);
      return {
        ...p,
        _key: key,
        phone,
        amount,
        tariff,
        totalWithFees,
        canPay,
      };
    });
  }, [pendingPayments, validatedFarmers, companyBankBalance]);

  const payableSummary = useMemo(() => {
    const payable = payableRows.filter((p) => p.canPay);
    const amountTotal = payable.reduce((sum, p) => sum + (p.amount || 0), 0);
    const totalWithFees = payable.reduce(
      (sum, p) => sum + (p.totalWithFees || 0),
      0
    );
    return {
      count: payable.length,
      amountTotal,
      totalWithFees,
    };
  }, [payableRows]);

  const narrate = (text) => {
    if (!voiceEnabled) return;
    try {
      if (typeof window === "undefined") return;
      const synth = window.speechSynthesis;
      if (!synth) return;
      const utter = new SpeechSynthesisUtterance(text);
      synth.cancel();
      synth.speak(utter);
    } catch (err) {
      console.warn("Speech synthesis unavailable", err);
    }
  };

  const setReview = (payload) =>
    setReviewState((prev) => ({
      ...prev,
      ...payload,
    }));

  const analyzePayment = async (payment) => {
    if (!payment) return { decision: "blocked" };
    const phone = resolvePhoneNumber(payment, validatedFarmers);
    const amount = Number(payment.Amount || 0);
    const ts = new Date();
    const key = getPaymentKey(payment);

    const startingStep = [
      {
        label: "Starting review",
        detail: `Checking ${phone || "unknown"} for ${amount.toLocaleString()} KES`,
      },
    ];
    setReview({
      currentKey: key,
      steps: startingStep,
      decision: "reviewing",
      historyStats: null,
      modelScore: null,
      flags: { history: false, model: false, duplicate: false },
      phone,
      payment,
    });

    const history = await fetchPhoneHistory(phone);
    const featureResult = buildFeatureVector({
      phone,
      amount,
      ts,
      history,
    });

    const historyFlag =
      featureResult.status !== "ok" ||
      (featureResult.historyStats &&
        Math.abs(featureResult.historyStats.amountZ || 0) >= 3);

    const duplicateCount = countSameDayPhoneDuplicates({
      pendingPayments,
      currentPaymentKey: key,
      phone,
      getPaymentKey,
      resolvePhoneNumber,
      resolveContext: validatedFarmers,
    });
    const duplicateFlag = duplicateCount > 0;

    const nextSteps = [
      ...startingStep,
      {
        label: "History check",
        detail:
          featureResult.status === "ok"
            ? `Seen ${featureResult.historyStats.countPrev} previous payments; z-score ${(
                featureResult.historyStats.amountZ || 0
              ).toFixed(2)}`
            : "No history found for this phone",
        flag: historyFlag,
      },
      {
        label: "Duplicate phone check",
        detail: duplicateFlag
          ? `⚠️ WARNING: Phone ${phone} has ${duplicateCount + 1} payment(s) scheduled for today`
          : `No duplicate payments for ${phone} today`,
        flag: duplicateFlag,
      },
    ];

    setReview({
      currentKey: key,
      steps: nextSteps,
      decision: "reviewing",
      historyStats: featureResult.historyStats || null,
      modelScore: null,
      flags: { history: historyFlag, model: false, duplicate: duplicateFlag },
      phone,
      payment,
    });

    const user = auth?.currentUser;
    let modelFlag = false;
    let modelScore = null;
    if (user) {
      try {
        const idToken = await user.getIdToken();
        const features =
          featureResult.status === "ok"
            ? featureResult.features
            : {
                amount,
                day_of_week: ts.getDay(),
                day_of_month: ts.getDate(),
                hour: ts.getHours(),
                mean_amount_prev: featureResult.historyStats?.meanPrev ?? 0,
                std_amount_prev: featureResult.historyStats?.stdPrev ?? 1,
                amount_zscore: featureResult.historyStats?.amountZ ?? 0,
                count_prev: featureResult.historyStats?.countPrev ?? 0,
                matches_dominant_window: 0,
                tx_pattern_window_other: 1,
                tx_pattern_window_weekly_window: 0,
              };

        const response = await fetch(`${API_BASE_URL}/api/ml/score-b2c-anomaly/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ features }),
        });

        const data = await response.json();
        if (response.ok && data?.status === "ok") {
          modelScore = Number(data.anomaly_score || 0);
          modelFlag = Boolean(data.is_anomaly);
          nextSteps.push({
            label: "Model verdict",
            detail: `IsolationForest score ${modelScore.toFixed(3)} • ${
              modelFlag ? "anomaly" : "normal"
            }`,
            flag: modelFlag,
          });
        } else {
          nextSteps.push({
            label: "Model verdict",
            detail: data?.error || "Model unavailable",
            flag: false,
          });
        }
      } catch (err) {
        console.error("Model call failed", err);
        nextSteps.push({
          label: "Model verdict",
          detail: "Unable to reach anomaly model",
          flag: false,
        });
      }
    } else {
      nextSteps.push({
        label: "Model verdict",
        detail: "Sign in to run anomaly model.",
        flag: false,
      });
    }

    const decision =
      historyFlag && modelFlag
        ? "blocked"
        : historyFlag || modelFlag || duplicateFlag
        ? "needs-approval"
        : "clear";

    const finalSteps = [
      ...nextSteps,
      {
        label: "Decision",
        detail:
          decision === "clear"
            ? "Looks safe — ready to pay"
            : decision === "blocked"
            ? "Flagged by history and model — skipping auto payment"
            : "Flagged once — needs approval",
        flag: decision !== "clear",
      },
    ];

    const voiceLine =
      decision === "clear"
        ? `All checks passed for ${payment.CloudName || payment.OtherName || "farmer"}.`
        : decision === "blocked"
        ? "This payment is blocked because both checks look risky."
        : "This payment needs your approval. One of the checks flagged an issue.";

    narrate(voiceLine);

    setReview({
      currentKey: key,
      steps: finalSteps,
      decision,
      historyStats: featureResult.historyStats || null,
      modelScore,
      flags: { history: historyFlag, model: modelFlag, duplicate: duplicateFlag },
      phone,
      payment,
    });

    return {
      decision,
      flags: { history: historyFlag, model: modelFlag, duplicate: duplicateFlag },
    };
  };

  const triggerB2CPayment = async ({
    amount,
    phone,
    name,
    otherName,
    paymentId,
    transactionCost,
  }) => {
    const user = auth?.currentUser;
    if (!user) {
      throw new Error("You must be logged in to make payments.");
    }

    const idToken = await user.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/mpesa/b2c-payment/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        phone_number: phone,
        amount: Math.round(Number(amount || 0)),
        remarks: `Payment to ${name || otherName || ""}`,
        occasion: "Farmer Payment",
        company_name: companyName,
        farmer_other_name: otherName || "",
        payment_id: paymentId || "",
        transaction_cost: Math.round(Number(transactionCost || 0)),
      }),
    });

    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(
        "Unable to reach payment service. Please try again in a moment."
      );
    }

    if (!response.ok || !data.success) {
      const backendError = data?.error || data?.message || "";

      let friendly = "Payment failed. Please try again.";

      if (response.status === 401) {
        friendly = "Your session has expired. Please sign in again to continue.";
      } else if (backendError.includes("OAuth token")) {
        friendly = "Unable to contact Safaricom at the moment. Please try again later.";
      } else if (backendError.toLowerCase().includes("invalid phone")) {
        friendly = "The phone number is invalid. Use format 254XXXXXXXXX.";
      } else if (
        backendError.includes("Amount must be") ||
        backendError.includes("Invalid amount")
      ) {
        friendly = "Amount must be a positive whole number in KES.";
      } else if (
        backendError.includes("Missing required environment variables")
      ) {
        friendly =
          "Payment service is not fully configured. Please contact the administrator.";
      } else if (backendError.toLowerCase().includes("network error")) {
        friendly =
          "Network problem while contacting M‑Pesa. Please check your connection and try again.";
      }

      throw new Error(friendly);
    }

    return data.data || data;
  };

  const handlePaySingle = async (payment, override = false) => {
    if (!companyName || !payment) return;

    const phone =
      payment.phone || resolvePhoneNumber(payment, validatedFarmers);
    if (!phone) {
      setStatusMessage("Cannot pay: missing phone number for this farmer.");
      return;
    }

    const amount = Number(payment.Amount || payment.amount || 0);
    const tariff = payment.tariff ?? calcTariff(amount, payment.Mode);
    const key = getPaymentKey(payment);
    if (inFlightPaymentKeysRef.current.has(key)) {
      return;
    }
    const displayName = payment.CloudName || payment.OtherName || "Farmer";
    const deduction = amount + tariff;

    if (deduction > Number(companyBankBalance || 0)) {
      setStatusMessage("Insufficient company bank balance for this payment.");
      return;
    }

    setPayingId(key);
    inFlightPaymentKeysRef.current.add(key);
    setStatusMessage(
      `Running fraud checks for KES ${amount.toLocaleString()} to ${displayName} on ${phone}...`
    );

    const review = override ? { decision: "clear" } : await analyzePayment(payment);
    if (review.decision === "blocked") {
      setStatusMessage(
        "Stopped: both history and model flagged this payment. Please double-check the record."
      );
      setPayingId(null);
      return;
    }

    if (review.decision === "needs-approval" && !override) {
      setStatusMessage(
        "Flagged once. Click Authorize to proceed or fix the record first."
      );
      setPayingId(null);
      return;
    }

    setStatusMessage(
      `Sending KES ${amount.toLocaleString()} to ${displayName} on ${phone}...`
    );

    try {
      const res = await triggerB2CPayment({
        amount,
        phone,
        name: payment.CloudName || payment.OtherName || "",
        otherName: payment.OtherName || payment.otherName || "",
        paymentId: key,
        transactionCost: tariff,
      });

      const desc =
        res?.ResponseDescription || res?.status || "Request sent to M‑Pesa";
      setStatusMessage(
        `Payment queued: KES ${amount.toLocaleString()} to ${displayName}. M‑Pesa response: ${desc}`
      );
      toast.success("Payment request sent to M‑Pesa");

      try {
        const newBalance = Number(companyBankBalance || 0) - deduction;
        await rtdbUpdate(
          rtdbRef(database, `Companies/${companyName}/AccountBalances/company_bank`),
          { balance: newBalance }
        );
      } catch (err) {
        console.error("Failed to update balance", err);
      }

      try {
        await rtdbSet(
          rtdbRef(database, `Companies/${companyName}/NewPaymentRecord/${key}`),
          null
        );
      } catch (err) {
        console.error("Failed to remove paid record", err);
      }
    } catch (err) {
      console.error(err);
      const friendly = err?.message || "Payment failed. Please try again.";
      setStatusMessage(`Payment error: ${friendly}`);
      toast.error(friendly);
    } finally {
      inFlightPaymentKeysRef.current.delete(key);
      setPayingId(null);
    }
  };

  const handlePayAll = async () => {
    if (!companyName || !payableRows.length) return;
    setPayAllBusy(true);
    let remainingBalance = Number(companyBankBalance || 0);
    for (const p of payableRows) {
      if (!p.canPay) continue;
      const deduction = p.totalWithFees || 0;
      if (deduction > remainingBalance) {
        setStatusMessage("Stopped: insufficient balance to continue paying all.");
        break;
      }
      await handlePaySingle(p);
      remainingBalance -= deduction;
    }
    setPayAllBusy(false);
  };

  const sortedPendingPayments = useMemo(() => {
    return [...payableRows].sort((a, b) =>
      comparePayments(a, b, sortField, sortDir, false)
    );
  }, [payableRows, sortField, sortDir]);

  const sortedFarmerPayments = useMemo(() => {
    return [...farmerPayments].sort((a, b) =>
      comparePayments(a, b, sortField, sortDir, true)
    );
  }, [farmerPayments, sortField, sortDir]);

  const isAdminView =
    role === "company" ||
    role === "employee" ||
    role === "admin" ||
    role === "companyAdmin";

  const activePaymentList = isAdminView ? sortedPendingPayments : sortedFarmerPayments;
  const totalPages = Math.max(1, Math.ceil(activePaymentList.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPayments = activePaymentList.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  const exportPaymentsToCsv = () => {
    const data = isAdminView ? pendingPayments : farmerPayments;
    if (!data.length) {
      toast.error("No payments to export");
      return;
    }

    const header = ["PaymentName", "PhoneNumber", "Amount", "Mode"];
    const rows = data.map((p) => {
      const farmerKey = p.OtherName || p.otherName || p.CloudName;
      const farmer =
        farmerKey && validatedFarmers ? validatedFarmers[farmerKey] : null;
      const resolvedMode = farmer?.Mode || farmer?.mode || p.Mode || "";

      return [
        p.CloudName || p.OtherName || p.ReceiptNo || p.Id || "",
        resolvePhoneNumber(p, validatedFarmers),
        Number(p.Amount || 0),
        resolvedMode,
      ];
    });

    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((cell) =>
            `"${String(cell ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `payments_${
      isAdminView ? "pending" : "history"
    }_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV generated");
  };

  const handleDelete = async (payment) => {
    if (!companyName) return;
    try {
      const key = payment._key || payment.Id;
      const path = `Companies/${companyName}/NewPaymentRecord/${key}`;
      await rtdbRemove(rtdbRef(database, path));
      toast.success("Payment removed");
    } catch (err) {
      console.error("delete payment", err);
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-cyan-700">Payments</h1>
        <p className="text-sm text-gray-500">
          Clean view: top banner and quick action buttons removed. Use
          the date selector below to load historical company payments.
        </p>
      </div>

      {/* Date selector for company historical records */}
      {isAdminView && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs text-gray-600">
              Show company payments for date
            </label>
            <input
              type="date"
              className="w-full border rounded px-3 py-2"
              value={companyDate}
              onChange={(e) => {
                setCompanyDate(e.target.value);
                if (e.target.value)
                  loadCompanyRecordsForDate(e.target.value);
                else setCompanyRecords([]);
              }}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600">
              Quick info
            </label>
            <div className="bg-white p-3 rounded border text-sm text-gray-600">
              Pick a date to view company payments saved under
              Companies - Year - Month - Day...
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600">
              Results
            </label>
            <div className="bg-white p-3 rounded border text-sm text-gray-600">
              {companyRecordsLoading
                ? "Loading…"
                : `${companyRecords.length} records`}
            </div>
          </div>
        </div>
      )}

      {/* Totals for admin */}
      {isAdminView && !compactView && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <SummaryCard title="Total Amount">
            <div className="text-lg font-semibold text-cyan-700">
              KES {pendingSummary.totalAmount.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {pendingPayments.length} pending
            </div>
          </SummaryCard>

          <SummaryCard title="Cumulative Total">
            <div className="text-lg font-semibold text-cyan-700">
              KES {pendingSummary.cumulativeTotal.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Amount + Tariff
            </div>
          </SummaryCard>

          <SummaryCard title="Tariff (We charge)">
            <div className="text-lg font-semibold text-cyan-700">
              KES {pendingSummary.totalTariff.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              M-Pesa &amp; KCB charges
            </div>
          </SummaryCard>

          <SummaryCard title="Tariff (Other means)">
            <div className="text-lg font-semibold text-cyan-700">
              KES {pendingSummary.standardTariff.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              What they'd pay otherwise
            </div>
          </SummaryCard>

          <SummaryCard title="Savings">
            <div className="text-lg font-semibold text-green-700">
              KES {pendingSummary.totalSavings.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {pendingSummary.savingsPct.toFixed(1)}% saved
            </div>
          </SummaryCard>
        </div>
      )}

      {isAdminView && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-gray-500">Company bank balance</div>
            <div className="text-lg font-semibold text-gray-800">
              KES {Number(companyBankBalance || 0).toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              Includes amount + transaction fee checks.
            </div>
          </div>

          <div className="bg-white border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-gray-500">
              Payable farmers ({payableSummary.count})
            </div>
            <div className="text-lg font-semibold text-gray-800">
              KES {Number(payableSummary.amountTotal || 0).toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              With fees: KES{" "}
              {Number(payableSummary.totalWithFees || 0).toLocaleString()}
            </div>
          </div>

          <div className="bg-white border rounded-lg p-4 shadow-sm space-y-2">
            <div className="text-xs text-gray-500">Status</div>
            <div className="text-sm text-gray-700 min-h-[40px]">
              {statusMessage || "Idle"}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePayAll}
                disabled={
                  payAllBusy ||
                  !payableSummary.count ||
                  payableSummary.totalWithFees > Number(companyBankBalance || 0)
                }
                className="px-4 py-2 rounded bg-cyan-600 text-white text-sm disabled:opacity-50"
              >
                {payAllBusy ? "Paying..." : "Pay all"}
              </button>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={voiceEnabled}
                  onChange={(e) => setVoiceEnabled(e.target.checked)}
                />
                Narrate
              </label>
            </div>
            <div className="text-[11px] text-gray-500">
              Pay buttons disable when balance is below amount + tariff.
            </div>
          </div>
        </div>
      )}

      {isAdminView && (
        <div className="mb-6 bg-white border rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-gray-700">Payment analyst</div>
            <div className="text-xs text-gray-500">
              {reviewState.decision === "idle"
                ? "Waiting"
                : reviewState.decision === "reviewing"
                ? "Analyzing"
                : reviewState.decision === "clear"
                ? "Cleared"
                : reviewState.decision === "needs-approval"
                ? "Needs approval"
                : "Blocked"}
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span className="px-2 py-1 rounded-full bg-gray-100 border">
                {reviewState.phone || "No phone selected"}
              </span>
              {reviewState.historyStats && (
                <>
                  <span>
                    Seen {reviewState.historyStats.countPrev} payments
                  </span>
                  <span>
                    z-score {(reviewState.historyStats.amountZ || 0).toFixed(2)}
                  </span>
                  <span>window {reviewState.historyStats.txWindow}</span>
                </>
              )}
              {reviewState.modelScore !== null && (
                <span>model score {Number(reviewState.modelScore).toFixed(3)}</span>
              )}
            </div>
            <div className="space-y-2">
              {reviewState.steps.length === 0 && (
                <p className="text-sm text-gray-600">
                  Select Pay on a row to run the fraud checks.
                </p>
              )}
              {reviewState.steps.map((step, idx) => (
                <div
                  key={`${step.label}-${idx}`}
                  className={`flex items-start gap-3 rounded-lg border px-3 py-2 ${
                    step.flag
                      ? "border-amber-200 bg-amber-50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="text-sm font-semibold text-gray-800">
                    {step.label}
                  </div>
                  <div className="text-xs text-gray-600">{step.detail}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  if (reviewState.payment) handlePaySingle(reviewState.payment, true);
                }}
                disabled={
                  reviewState.decision !== "needs-approval" ||
                  payingId === reviewState.currentKey
                }
                className="px-3 py-2 rounded bg-emerald-600 text-white text-sm disabled:opacity-50"
              >
                Authorize & Pay
              </button>
              <button
                onClick={() =>
                  setReviewState({
                    currentKey: null,
                    steps: [],
                    decision: "idle",
                    historyStats: null,
                    modelScore: null,
                    flags: { history: false, model: false, duplicate: false },
                    phone: "",
                    payment: null,
                  })
                }
                className="px-3 py-2 rounded border text-sm"
              >
                Reset review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company records for chosen date */}
      {isAdminView && !compactView && (
        <div className="mb-6 bg-white rounded-lg border shadow-sm overflow-hidden">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-700">
                Company payments for {companyDate || "—"}
              </div>
              <div className="text-xs text-gray-400">
                {companyRecordsLoading
                  ? "Loading…"
                  : `${companyRecords.length} items`}
              </div>
            </div>
          </div>

          <div>
            {companyRecords.length === 0 ? (
              <div className="p-4 text-gray-500">
                No records for the selected date.
              </div>
            ) : (
              <>
                <div className="p-3 border-b bg-slate-50 flex flex-wrap gap-3 items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Records for:{" "}
                    <span className="font-semibold text-gray-800">
                      {companyDate}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">
                      Filter by Client ID
                    </label>
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value={selectedClientId}
                      onChange={(e) =>
                        setSelectedClientId(e.target.value)
                      }
                    >
                      <option value="">All</option>
                      {Array.from(
                        new Set(
                          companyRecords
                            .map(
                              (p) =>
                                p.Client_Id ||
                                p.ClientId ||
                                p.ClientID ||
                                ""
                            )
                            .filter(Boolean)
                        )
                      ).map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
                  <div className="lg:col-span-2 bg-white border rounded-lg p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Amount over time
                    </h4>
                    {chartRecords.length === 0 ? (
                      <div className="text-sm text-gray-500">
                        No data for selected client/date.
                      </div>
                    ) : (
                      <div className="h-64">
                        <ResponsiveContainer
                          width="100%"
                          height="100%"
                        >
                          <LineChart
                            data={chartRecords}
                            margin={{
                              top: 10,
                              right: 20,
                              left: 0,
                              bottom: 10,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="time"
                              tick={{ fontSize: 11 }}
                            />
                            <YAxis tick={{ fontSize: 11 }} />
                            <RechartsTooltip />
                            <ReferenceLine
                              y={referenceAmount}
                              stroke="#94a3b8"
                              strokeDasharray="4 4"
                              label="Top amount"
                            />
                            <Line
                              type="monotone"
                              dataKey="amount"
                              stroke="#0ea5e9"
                              strokeWidth={2}
                              dot
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Payment modes (placeholder)
                    </h4>
                    {pieData.length === 0 ? (
                      <div className="text-sm text-gray-500">
                        No data for selected client/date.
                      </div>
                    ) : (
                      <div className="h-64">
                        <ResponsiveContainer
                          width="100%"
                          height="100%"
                        >
                          <PieChart>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              nameKey="name"
                              outerRadius={80}
                              label
                            >
                              {pieData.map((entry, index) => (
                                <Cell
                                  key={`cell-${entry.name}`}
                                  fill={
                                    pieColors[
                                      index % pieColors.length
                                    ]
                                  }
                                />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                {companyRecords.map((p) => (
                  <div
                    key={p.Id}
                    className="p-3 border-b flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium text-gray-800">
                        {p.CloudName ||
                          p.OtherName ||
                          p.ReceiptNo ||
                          p.Id}
                      </div>
                      <div className="text-xs text-gray-500">
                        {p.Details}
                      </div>
                      <div className="text-xs text-gray-400">
                        {p.TransactionDate
                          ? new Date(
                              p.TransactionDate
                            ).toLocaleString()
                          : "—"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="font-semibold text-cyan-700">
                        KES{" "}
                        {Number(p.Amount || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-400">
                        {p.Mode || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Pending / Farmer lists */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-gray-700">
              {isAdminView ? "Pending Payments" : "Your Payments"}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportPaymentsToCsv}
                className="px-3 py-1 rounded bg-cyan-600 text-white text-xs"
              >
                Generate CSV
              </button>
              <div className="text-xs text-gray-400">
                {isAdminView
                  ? pendingLoading
                    ? "Loading…"
                    : `${pendingPayments.length} items`
                  : farmerLoading
                  ? "Loading…"
                  : `${farmerPayments.length} items`}
              </div>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">Sort:</span>
            {[
              { key: "name", label: "Name" },
              { key: "date", label: "Date" },
              { key: "amount", label: "Amount" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => {
                  if (sortField === key) {
                    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                  } else {
                    setSortField(key);
                    setSortDir("asc");
                  }
                  setCurrentPage(1);
                }}
                className={`px-2 py-1 rounded border text-xs ${
                  sortField === key
                    ? "bg-cyan-600 text-white border-cyan-600"
                    : "bg-white text-gray-600 border-gray-300"
                }`}
              >
                {label}{" "}
                {sortField === key ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1 text-xs text-gray-500">
              <span>Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded px-2 py-1 text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          {isAdminView ? (
            pendingPayments.length === 0 ? (
              <div className="p-4 text-gray-500">
                No pending payments.
              </div>
            ) : (
              paginatedPayments.map((p) => (
                <PaymentRow
                  key={p._key || p.Id}
                  p={p}
                  isAdminView={true}
                  onEdit={(pay) => setEditPayment(pay)}
                  onDelete={(pay) => handleDelete(pay)}
                  onPay={(pay) => handlePaySingle(pay)}
                  compact={compactView}
                  validatedFarmers={validatedFarmers}
                  canPay={p.canPay}
                  paying={payingId === (p._key || p.Id)}
                />
              ))
            )
          ) : farmerPayments.length === 0 ? (
            <div className="p-4 text-gray-500">No payments yet.</div>
          ) : (
            paginatedPayments.map((p) => (
              <div
                key={p.Id}
                className="p-3 border-b flex items-center justify-between"
              >
                <div>
                  <div className="font-medium text-gray-800">
                    {p.ReceiptNo || p.Id}
                  </div>
                  <div className="text-xs text-gray-500">
                    {p.Details}
                  </div>
                </div>

                <div className="text-right">
                  <div className="font-semibold text-cyan-700">
                    KES {Number(p.Amount || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-400">
                    {p.TransactionDate
                      ? new Date(
                          p.TransactionDate
                        ).toLocaleDateString()
                      : "—"}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="p-3 border-t flex items-center justify-between text-xs text-gray-600">
            <span>
              Page {safePage} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-2 py-1 rounded border disabled:opacity-40"
              >
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (n) =>
                    n === 1 ||
                    n === totalPages ||
                    Math.abs(n - safePage) <= 1
                )
                .reduce((acc, n, idx, arr) => {
                  if (idx > 0 && n - arr[idx - 1] > 1) {
                    acc.push(`ellipsis-${n}`);
                  }
                  acc.push(n);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === "string" ? (
                    <span key={item} className="px-1">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setCurrentPage(item)}
                      className={`px-2 py-1 rounded border ${
                        safePage === item
                          ? "bg-cyan-600 text-white border-cyan-600"
                          : ""
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={safePage === totalPages}
                className="px-2 py-1 rounded border disabled:opacity-40"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <RecordPaymentModal
        open={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        farmer={recordTargetFarmer}
        companyName={companyName}
        validatedFarmers={validatedFarmers}
      />

      <EditPaymentModal
        open={!!editPayment}
        onClose={() => setEditPayment(null)}
        payment={editPayment}
        companyName={companyName}
        onSaved={(updated) => {
          setPendingPayments((prev) =>
            prev.map((p) =>
              (p._key || p.Id) === (updated._key || updated.Id)
                ? { ...p, ...updated }
                : p
            )
          );
        }}
      />

      <PaymentValidationModal
        open={!!crossPayment}
        onClose={() => setCrossPayment(null)}
        payment={crossPayment}
        resolvePhoneNumber={resolvePhoneNumber}
        companyName={companyName}
      />
    </div>
  );
};

export default PaymentsPage;
